<script setup>
import { computed } from 'vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import StreamPlayer from '@/components/ui/StreamPlayer.vue'
import Spinner from '@/components/ui/Spinner.vue'
import { mediaTypeMeta } from './mediaflowMeta'

/**
 * Quick look at a single source: StreamPlayer for live sources, the public
 * player iframe for VOD (same URLs as before). The frame takes the media's
 * aspect ratio, so there is no letterbox band under the picture.
 */
const props = defineProps({
	modelValue: { type: Boolean, default: false },
	media: { type: Object, default: () => ({ name: '' }) },
	/** Live sources: the resolved stream + its pulse. */
	stream: { type: Object, default: null },
	mediaPulse: { type: Object, default: () => ({}) },
	/** VOD sources: player URL. */
	url: { type: String, default: '' },
	portrait: { type: Boolean, default: false },
	loading: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'close'])

const visible = computed({
	get: () => props.modelValue,
	set: (v) => emit('update:modelValue', v),
})
const meta = computed(() => mediaTypeMeta(props.media?.type))
const isLive = computed(() => props.media?.type === 'live')
const chip = computed(() => [meta.value.label, isLive.value ? props.stream?.name : ''].filter(Boolean).join(' · '))
</script>

<template>
	<ModalBox
		v-model="visible"
		:title="media?.name || 'Preview'"
		size="sm"
		:wrapper-classes="portrait ? 'w-full md:!w-[400px]' : 'w-full md:!w-[720px]'"
		placement="center"
		header-class="!p-3 !pl-4 items-center gap-2"
		@close="emit('close')"
	>
		<template #header>
			<span v-if="chip" class="inline-flex h-6 shrink-0 items-center rounded-full bg-surface-3 px-2.5 text-xs text-surface-8" data-testid="media-preview-type">{{ chip }}</span>
		</template>
		<div class="px-3 pb-3">
			<div
				class="mf-preview-player relative w-full overflow-hidden bg-black"
				:class="portrait ? 'mx-auto aspect-[9/16] max-h-[70vh]' : 'aspect-video'"
				data-testid="media-preview-frame"
			>
				<template v-if="isLive">
					<div v-if="stream" class="absolute inset-0 flex items-center justify-center">
						<StreamPlayer
							:stream="stream"
							:media-pulse="mediaPulse"
							:stream-url="`wss://fake/${stream.key}/mse_ld?tracks=v1a1`"
							:multiview="false"
							:obs="true"
							:ref-link="2"
							:portrait="portrait"
						/>
					</div>
					<div v-else class="absolute inset-0 flex items-center justify-center text-tiny text-surface-8">
						<Spinner v-if="loading" text="Loading stream…" classes="text-surface-8" spinner-color="var(--c-dark-8)" spinner-size="14px" />
						<span v-else>This livestream isn't in your account anymore.</span>
					</div>
				</template>
				<iframe v-else-if="url" :src="url" class="absolute inset-0 h-full w-full border-0" allowfullscreen :title="`Preview of ${media?.name || 'source'}`" />
				<div v-else class="absolute inset-0 flex items-center justify-center text-tiny text-surface-8">No preview available.</div>
			</div>
		</div>
	</ModalBox>
</template>

<style scoped>
/* Player controls: one centred group, shown on hover / keyboard focus only. */
.mf-preview-player :deep(.btn_pause),
.mf-preview-player :deep(.btn_mute),
.mf-preview-player :deep(.btn_fullscreen) {
	opacity: 0;
	transition: opacity 0.15s ease;
	bottom: 12px;
	right: auto;
}
.mf-preview-player:hover :deep(.btn_pause),
.mf-preview-player:hover :deep(.btn_mute),
.mf-preview-player:hover :deep(.btn_fullscreen),
.mf-preview-player:focus-within :deep(.btn_pause),
.mf-preview-player:focus-within :deep(.btn_mute),
.mf-preview-player:focus-within :deep(.btn_fullscreen) {
	opacity: 1;
}
.mf-preview-player :deep(.btn_pause) {
	left: calc(50% - 66px);
}
.mf-preview-player :deep(.btn_mute) {
	left: calc(50% - 18px);
}
.mf-preview-player :deep(.btn_fullscreen) {
	left: calc(50% + 30px);
}
</style>
